import { Injectable } from "@angular/core";
import { Http, RequestOptions, Headers } from "@angular/http";
import { ActivatedRouteSnapshot } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { Config } from "src/app/config";
import { ServerResponse, SERVERRESPONSERESULT } from "src/app/models/serverresponse";
import { ServerResponseAdminLogin } from "src/app/models/serverresponse_adminlogin";
import { AdminUser } from "src/app/models/adminuser";
import { ModalDialogService } from "src/app/modals/modaldialog/modaldialog.service";
import { AdminLoginComponent } from "src/app/modals/adminlogin/adminlogin.component";

@Injectable()
export class AdminAuthService {
  private adminUser: AdminUser;

  constructor(private http: Http, private modalDialogService: ModalDialogService) {
    this.adminUser = null;
  }

  /**
   * Logs in with a backoffice account. With no `code`, the answer may be `askCode`: send the same
   * username and password again with the two-factor code. A login that passes opens the admin
   * session on this domain.
   */
  login(user: string, password: string, code: string): Observable<ServerResponseAdminLogin> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "adminlogin",
      user: user,
      password: password,
      code: code,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = <ServerResponseAdminLogin>response.json();
          if (json.result == SERVERRESPONSERESULT.OK && !json.askCode) {
            this.adminUser = {
              id: +json.id,
              username: json.username,
            };
          }
          return json;
        } catch (err) {
          console.error(err);
          let ret: ServerResponseAdminLogin = {
            result: SERVERRESPONSERESULT.Error,
            error: "Server error",
          }
          return ret;
        }
      }),
    );
  }

  /**
   * Shows the admin login in a modal over the page, for a save that found no admin session. The
   * page keeps its work. Answers true once the admin has logged in, false if they closed it.
   */
  askLogin(message: string): Observable<boolean> {
    const result = new Subject<boolean>();
    let loggedIn = false;
    const modal = this.modalDialogService.createModal();
    modal.setTitle(Config.APP_NAME);
    const component = modal.setBodyComponent(AdminLoginComponent);
    component.message = message;
    component.loggedIn.subscribe(() => {
      loggedIn = true;
      modal.close();
    });
    modal.setButtonCancel();
    modal.show().subscribe({
      next: button => modal.close(),
      complete: () => {
        this.modalDialogService.dismissModal(modal);
        result.next(loggedIn);
        result.complete();
      },
    });
    return result.asObservable();
  }

  /**
   * Spends a backoffice ticket (one use, 5 minutes), which opens the admin session on this
   * domain, as the backoffice's Edit, View and Evaluate buttons do.
   */
  exchangeTicket(ticket: string): Observable<ServerResponseAdminLogin> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "adminticket",
      ticket: ticket,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = <ServerResponseAdminLogin>response.json();
          if (json.result == SERVERRESPONSERESULT.OK) {
            this.adminUser = {
              id: +json.id,
              username: json.username,
            };
          }
          return json;
        } catch (err) {
          console.error(err);
          let ret: ServerResponseAdminLogin = {
            result: SERVERRESPONSERESULT.Error,
            error: "Server error",
          }
          return ret;
        }
      }),
    );
  }

  /**
   * `checkLogin`, after spending the route's `?ticket=` when it carries one. A failed exchange
   * is not an error by itself: a session opened earlier still counts.
   */
  checkLoginWithTicket(route: ActivatedRouteSnapshot): Observable<boolean> {
    const ticket = route.queryParams["ticket"];
    if (!ticket) {
      return this.checkLogin();
    }
    return this.exchangeTicket(ticket).pipe(switchMap(() => this.checkLogin()));
  }

  logout(): Observable<ServerResponse> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "adminlogout",
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = <ServerResponse>response.json();
          if (json.result == SERVERRESPONSERESULT.OK) {
            this.adminUser = null;
          }
          return json;
        } catch (err) {
          console.error(err);
          let ret: ServerResponse = {
            result: SERVERRESPONSERESULT.Error,
            error: "Server error",
          }
          return ret;
        }
      }),
    );
  }

  checkLogin(): Observable<boolean> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "adminchecklogin",
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          if (json["login"] === true) {
            this.adminUser = {
              id: +json.id,
              username: json.username,
            };
            return true;
          }
          return false;
        } catch (err) {
          console.error(err);
          return false;
        }
      }),
    );
  }

  get currentUser(): AdminUser {
    return this.adminUser;
  }
}
